import type { ComponentType, ReactNode } from 'react'
import type { LucideProps } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AnimatedNumber } from './AnimatedNumber'

type Tone = 'royal' | 'periwinkle' | 'violet' | 'amber' | 'green' | 'rose' | 'neutral'

const TONE_STYLES: Record<Tone, { chip: string; icon: string; bar: string; numberAccent: string }> = {
  royal:      { chip: 'bg-brand-royal/12',       icon: 'text-brand-royal',       bar: 'from-brand-royal to-brand-periwinkle',       numberAccent: 'text-foreground' },
  periwinkle: { chip: 'bg-brand-periwinkle/15',  icon: 'text-brand-periwinkle',  bar: 'from-brand-periwinkle to-brand-royal',       numberAccent: 'text-foreground' },
  violet:     { chip: 'bg-violet-500/12',        icon: 'text-violet-500',        bar: 'from-violet-500 to-violet-300',              numberAccent: 'text-foreground' },
  amber:      { chip: 'bg-amber-500/12',         icon: 'text-amber-600 dark:text-amber-400', bar: 'from-amber-500 to-amber-300',    numberAccent: 'text-amber-700 dark:text-amber-400' },
  green:      { chip: 'bg-emerald-500/12',       icon: 'text-emerald-600 dark:text-emerald-400', bar: 'from-emerald-500 to-emerald-300', numberAccent: 'text-foreground' },
  rose:       { chip: 'bg-rose-500/12',          icon: 'text-rose-500',          bar: 'from-rose-500 to-rose-300',                  numberAccent: 'text-rose-600 dark:text-rose-400' },
  neutral:    { chip: 'bg-muted',                icon: 'text-muted-foreground',  bar: 'from-muted-foreground/50 to-muted-foreground/20', numberAccent: 'text-foreground' },
}

interface StatCardProps {
  label: string
  /** Numeric value — animates count-up on mount/change */
  value: number
  /** Optional formatter — e.g. (n) => `$${n}` */
  format?: (n: number) => string
  /** Optional supporting line (delta, context) */
  hint?: ReactNode
  icon?: ComponentType<LucideProps>
  tone?: Tone
  /** Optional click handler — turns the card into a button */
  onClick?: () => void
  className?: string
}

/**
 * Premium dashboard stat card.
 *
 * Visual: large numeric headline (count-up), small uppercase label,
 * optional hint line, colored icon chip, and a thin bottom gradient bar
 * that turns the card into a "scoreboard tile" instead of a flat box.
 *
 * Pair multiple StatCards in a `.stagger-fade` parent for a satisfying
 * cascading entrance.
 */
export function StatCard({
  label,
  value,
  format,
  hint,
  icon: Icon,
  tone = 'royal',
  onClick,
  className,
}: StatCardProps) {
  const t = TONE_STYLES[tone]
  const Tag = onClick ? 'button' : 'div'

  return (
    <Tag
      onClick={onClick}
      className={cn(
        'group relative overflow-hidden rounded-xl border bg-card text-left shadow-tinted transition-all duration-200',
        '[transition-timing-function:var(--ease-out,cubic-bezier(0.23,1,0.32,1))]',
        onClick && 'press-scale hover:border-brand-periwinkle/40 hover:-translate-y-0.5 hover:shadow-tinted-lg cursor-pointer',
        className,
      )}
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
          {Icon && (
            <span className={cn('h-7 w-7 rounded-lg flex items-center justify-center shrink-0', t.chip)}>
              <Icon className={cn('h-3.5 w-3.5', t.icon)} />
            </span>
          )}
        </div>
        <p className={cn('text-2xl sm:text-3xl font-heading font-bold tabular-nums tracking-tight', t.numberAccent)}>
          <AnimatedNumber value={value} format={format} />
        </p>
        {hint && <p className="text-xs text-muted-foreground mt-1.5">{hint}</p>}
      </div>
      {/* Bottom gradient bar — subtle scoreboard accent */}
      <div className={cn('absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r opacity-70', t.bar)} aria-hidden="true" />
    </Tag>
  )
}
